/**
 * Unified File Upload Service
 * Consolidates upload functionality from DocumentUploadManager and DDDoc systems
 */

import { OPFSManager } from '../opfs/OPFSManager';
import { IndexedDBService } from '../indexeddb/IndexedDBManager';

export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

export interface UnifiedUploadItem {
  id: string;
  file: File;
  metadata: FileMetadata;
  status:
    | 'pending'
    | 'storing'
    | 'stored'
    | 'uploading'
    | 'uploaded'
    | 'failed';
  progress: number;
  error?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UploadConfig {
  endpoint: string;
  bearerToken?: string;
  headers?: Record<string, string>;
  maxRetries?: number;
  chunkSize?: number;
  batchSize?: number; // Number of files to upload concurrently in each batch
}

export class UnifiedFileUploadService {
  private static instance: UnifiedFileUploadService;
  private opfsManager: OPFSManager;
  private indexedDBService: IndexedDBService;
  private uploadQueue = new Map<string, UnifiedUploadItem>();
  private eventListeners = new Map<
    string,
    Set<(item: UnifiedUploadItem) => void>
  >();
  private worker: Worker | null = null;
  private uploadConfig: UploadConfig | null = null;

  private constructor() {
    this.opfsManager = OPFSManager.getInstance();
    this.indexedDBService = IndexedDBService.getInstance();
    // Initialize worker so it lives independently from UI components
    this.initWorker();
  }

  public static getInstance(): UnifiedFileUploadService {
    if (!UnifiedFileUploadService.instance) {
      UnifiedFileUploadService.instance = new UnifiedFileUploadService();
    }
    return UnifiedFileUploadService.instance;
  }

  /**
   * Configure upload endpoint and authentication
   * This should be called once during app initialization or when upload context changes
   */
  public setUploadConfig(config: UploadConfig): void {
    this.uploadConfig = config;
  }

  /**
   * Add raw File objects to the upload worker queue.
   *
   * This method creates UnifiedUploadItem wrappers internally and returns them via Promise.
   * Use this when you have raw File objects from file inputs and need the created items.
   *
   * @param files - Array of File objects to upload
   * @param config - Optional upload configuration (endpoint, headers, etc.)
   * @returns Promise that resolves to array of created UnifiedUploadItem objects
   *
   * @example
   * ```typescript
   * const items = await uploadService.addFiles(fileList, config);
   * items.forEach(item => console.log('Created item:', item.id));
   * ```
   */
  public async addFiles(
    files: File[],
    config?: UploadConfig
  ): Promise<UnifiedUploadItem[]> {
    if (!files || files.length === 0) {
      throw new Error('Files array is required and cannot be empty');
    }

    const items = files.map((file) => {
      const fileId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      return {
        id: fileId,
        file,
        metadata: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        },
        status: 'pending' as const,
        progress: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    });

    this._addItemsToWorker(items, config);
    return items;
  }

  /**
   * Add pre-constructed UnifiedUploadItem objects to the upload worker queue.
   *
   * This is a fire-and-forget method that queues items without waiting for completion.
   * Use event listeners to monitor upload progress and completion.
   *
   * @param items - Array of pre-constructed UnifiedUploadItem objects
   * @param config - Optional upload configuration (endpoint, headers, etc.)
   *
   * @example
   * ```typescript
   * const items = files.map(file => createUploadItem(file));
   * uploadService.addItems(items, config);
   * // Subscribe to events for progress monitoring
   * uploadService.addEventListener('progress', handleProgress);
   * uploadService.addEventListener('complete', handleComplete);
   * ```
   */
  public addItems(items: UnifiedUploadItem[], config?: UploadConfig): void {
    this._addItemsToWorker(items, config);
  }

  /**
   * Private implementation method that handles the actual worker communication.
   * This is used by all public methods to avoid code duplication.
   */
  private _addItemsToWorker(
    items: UnifiedUploadItem[],
    config?: UploadConfig
  ): void {
    // Allow using previously set upload config if none provided
    const effectiveConfig: UploadConfig | null = config ?? this.uploadConfig;

    // Validate upload configuration early to avoid silent worker failures
    if (!effectiveConfig || typeof effectiveConfig !== 'object') {
      const log = {
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'Upload config is required',
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));
      throw new Error('Upload config is required');
    }

    if (
      typeof effectiveConfig.endpoint !== 'string' ||
      !effectiveConfig.endpoint.trim()
    ) {
      const log = {
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'Upload endpoint is missing or empty',
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));
      throw new Error('Upload endpoint is required');
    }

    // Normalize headers & retries (defensive defaults)
    const normalizedHeaders =
      effectiveConfig.headers && typeof effectiveConfig.headers === 'object'
        ? effectiveConfig.headers
        : {};
    const normalizedMaxRetries =
      Number.isFinite(effectiveConfig.maxRetries) &&
      effectiveConfig.maxRetries! > 0
        ? effectiveConfig.maxRetries!
        : 3;

    const normalizedBatchSize =
      Number.isFinite(effectiveConfig.batchSize) &&
      effectiveConfig.batchSize! > 0
        ? effectiveConfig.batchSize!
        : 3;

    if (!this.worker) {
      this.initWorker();
    }

    if (!this.worker) {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Worker not available, running in test mode',
        error: null,
        stack_trace: null,
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));

      // In test mode, still add items to queue for testing purposes
      items.forEach((item) => {
        if (this.uploadQueue.has(item.id)) {
          const dupLog = {
            t: new Date().toISOString(),
            severity: 'WARNING',
            message: 'Duplicate file ID detected, skipping',
            error: null,
            stack_trace: null,
            trace_id: item.id,
          };
          console.log(JSON.stringify(dupLog));
          return;
        }
        // Keep items as pending in test mode to avoid implying storage happened
        item.status = 'pending';
        this.uploadQueue.set(item.id, item);
        this.notifyListeners('added', item);
      });

      return;
    }

    const acceptedItems: UnifiedUploadItem[] = [];

    // Add items to local queue with 'storing' status, skip duplicates
    items.forEach((item) => {
      if (this.uploadQueue.has(item.id)) {
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'Duplicate file ID detected, skipping',
          error: null,
          stack_trace: null,
          trace_id: item.id,
        };
        console.log(JSON.stringify(log));
        return;
      }
      item.status = 'storing';
      this.uploadQueue.set(item.id, item);
      this.notifyListeners('added', item);
      acceptedItems.push(item);
    });

    if (acceptedItems.length === 0) {
      return;
    }

    // Send all files to worker as a batch for storage and upload
    const filesData = acceptedItems.map((item) => ({
      id: item.id,
      file: item.file, // Actual File object, not ArrayBuffer
      metadata: item.metadata,
    }));

    this.worker!.postMessage({
      type: 'store-and-upload-batch',
      payload: {
        filesData,
        uploadConfig: {
          endpoint: effectiveConfig.endpoint.trim(),
          bearerToken: effectiveConfig.bearerToken,
          headers: normalizedHeaders,
          maxRetries: normalizedMaxRetries,
          concurrency: normalizedBatchSize, // Controlled via config.batchSize (default 3)
        },
      },
    });
  }

  /**
   * Remove file from upload queue
   */
  public async removeFile(fileId: string): Promise<boolean> {
    const item = this.uploadQueue.get(fileId);
    if (!item) {
      return false;
    }

    try {
      // Clean up storage - try both but don't fail if one fails
      await this.opfsManager.deleteFile(fileId).catch((err) => {
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'OPFS delete failed',
          error: err instanceof Error ? err.message : String(err),
          stack_trace: err instanceof Error ? (err.stack ?? null) : null,
          trace_id: fileId,
        };
        console.log(JSON.stringify(log));
        return false;
      });

      await this.indexedDBService.deleteFile(fileId).catch((err) => {
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'IndexedDB delete failed',
          error: err instanceof Error ? err.message : String(err),
          stack_trace: err instanceof Error ? (err.stack ?? null) : null,
          trace_id: fileId,
        };
        console.log(JSON.stringify(log));
        return false;
      });

      // Remove from queue even if storage cleanup partially failed
      this.uploadQueue.delete(fileId);
      this.notifyListeners('removed', item);

      return true;
    } catch (error) {
      const log = {
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'Failed to remove file',
        error: error instanceof Error ? error.message : String(error),
        stack_trace: error instanceof Error ? (error.stack ?? null) : null,
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));
      return false;
    }
  }

  /**
   * Get all files in queue
   */
  public getAllFiles(): UnifiedUploadItem[] {
    return Array.from(this.uploadQueue.values());
  }

  /**
   * Get file by ID
   */
  public getFile(fileId: string): UnifiedUploadItem | null {
    return this.uploadQueue.get(fileId) || null;
  }

  /**
   * Add event listener
   */
  public addEventListener(
    event: string,
    callback: (item: UnifiedUploadItem) => void
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  /**
   * Remove event listener
   */
  public removeEventListener(
    event: string,
    callback: (item: UnifiedUploadItem) => void
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Upload a single file by ID
   */
  public async uploadSingleFile(
    fileId: string,
    config?: UploadConfig
  ): Promise<void> {
    const item = this.uploadQueue.get(fileId);
    if (!item) {
      throw new Error(`File with ID ${fileId} not found`);
    }

    // Use the unified enqueue method
    this.enqueueToWorker(item.id, config);
    return Promise.resolve();
  }

  /**
   * Validate batch size (should not exceed 500MB)
   */
  public validateBatchSize(fileIds: string[]): {
    valid: boolean;
    sizeMB: number;
  } {
    const totalBytes = fileIds.reduce((sum, id) => {
      const item = this.uploadQueue.get(id);
      return sum + (item?.file.size || 0);
    }, 0);

    const sizeMB = totalBytes / (1024 * 1024);
    return {
      valid: sizeMB <= 500,
      sizeMB,
    };
  }

  /**
   * Get pending files that can be uploaded
   */
  public getPendingFiles(): UnifiedUploadItem[] {
    return Array.from(this.uploadQueue.values()).filter(
      (item) => item.status === 'stored' || item.status === 'pending'
    );
  }

  /**
   * Retry upload for a specific file
   * @throws {Error} If the file with the given ID is not found
   */
  public retryUpload(fileId: string, config?: UploadConfig): void {
    const item = this.uploadQueue.get(fileId);
    if (!item) {
      const log = {
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'File not found for retry',
        error: `File with ID ${fileId} does not exist in upload queue`,
        trace_id: fileId,
      };
      console.log(JSON.stringify(log));
      throw new Error(`File with ID ${fileId} not found for retry`);
    }

    item.status = 'stored';
    item.progress = 0;
    item.error = undefined;
    item.updatedAt = new Date();

    // Notify listeners about the status change
    this.notifyListeners('stored', item);

    this.enqueueToWorker(fileId, config);
  }

  /**
   * Enqueue a file to the worker for upload
   * This is called automatically when files are added if uploadConfig is set
   */
  private enqueueToWorker(fileId: string, config?: UploadConfig): void {
    const item = this.uploadQueue.get(fileId);
    if (!item) {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Cannot enqueue file - not found in queue',
        error: null,
        stack_trace: null,
        trace_id: null,
        fileId,
      };
      console.log(JSON.stringify(log));
      return;
    }

    // Prevent duplicate enqueueing - only enqueue if in 'stored' or 'pending' state
    // Skip if already uploading or uploaded to prevent duplicate requests
    if (item.status === 'uploading' || item.status === 'uploaded') {
      const log = {
        t: new Date().toISOString(),
        severity: 'INFO',
        message: 'Skipping enqueue - already processing or completed',
        error: null,
        stack_trace: null,
        trace_id: null,
        fileName: item.metadata.name,
        status: item.status,
      };
      console.log(JSON.stringify(log));
      return;
    }

    const effectiveConfig: UploadConfig | null = config ?? this.uploadConfig;
    if (!effectiveConfig) {
      const log = {
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'Upload config is required to enqueue file',
        trace_id: fileId,
      };
      console.log(JSON.stringify(log));
      return;
    }

    if (!this.worker) {
      this.initWorker();
    }

    // If worker is still not available (test environment), skip worker upload
    if (!this.worker) {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Worker not available, skipping upload',
        error: null,
        stack_trace: null,
        trace_id: item.metadata.name,
      };
      console.log(JSON.stringify(log));
      return;
    }

    // Calculate timeout based on file size
    const timeout = this.calculateFileTimeout(item.file.size);
    const concurrency =
      Number.isFinite(effectiveConfig.batchSize) &&
      effectiveConfig.batchSize! > 0
        ? effectiveConfig.batchSize!
        : 3;

    // Enqueue file for worker to process in FIFO order
    this.worker.postMessage({
      type: 'enqueue',
      payload: {
        id: item.id,
        endpoint: effectiveConfig.endpoint,
        bearerToken: effectiveConfig.bearerToken,
        headers: effectiveConfig.headers,
        timeout,
        maxRetries: effectiveConfig.maxRetries ?? 3,
        concurrency,
      },
    });
  }

  /**
   * Calculate upload timeout based on file size
   * Uses conservative upload speed assumptions to handle slower networks and large files
   *
   * @param fileSize - Size of file in bytes
   * @returns Timeout in milliseconds
   */
  private calculateFileTimeout(fileSize: number): number {
    const BASE_TIMEOUT_MS = 60000; // 60 seconds base for connection/handshake
    // Conservative speed assumption for slower networks
    // 50 KB/s = 50 * 1024 bytes/second = 51,200 bytes/second = 51.2 bytes/millisecond
    // This is intentionally conservative to accommodate slower network conditions.
    // For modern networks, actual speeds are typically much higher (e.g., 500+ KB/s)
    const UPLOAD_SPEED_BYTES_PER_MS = (50 * 1024) / 1000; // 50 KB/s = 51.2 bytes/ms
    const MAX_TIMEOUT_MS = 1800000; // 30 minutes maximum for very large files
    const MIN_TIMEOUT_MS = 120000; // 2 minutes minimum for any file

    // Calculate expected upload time and add 50% buffer for network variability
    const expectedUploadTime = fileSize / UPLOAD_SPEED_BYTES_PER_MS;
    const timeoutWithBuffer = expectedUploadTime * 1.5;

    // Apply base timeout, minimum, and maximum constraints
    const calculatedTimeout = BASE_TIMEOUT_MS + timeoutWithBuffer;
    const constrainedTimeout = Math.min(
      Math.max(calculatedTimeout, MIN_TIMEOUT_MS),
      MAX_TIMEOUT_MS
    );

    return Math.round(constrainedTimeout);
  }

  private initWorker(): void {
    if (this.worker) return;

    // Check if Worker is available (not available in test environments)
    if (typeof Worker === 'undefined') {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Worker not available, running in test mode',
        error: null,
        stack_trace: null,
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));
      return;
    }

    try {
      const workerBlob = new Blob([`${this.getWorkerCode()}`], {
        type: 'application/javascript',
      });
      this.worker = new Worker(URL.createObjectURL(workerBlob));

      this.worker.addEventListener('message', (ev: MessageEvent) => {
        const data = ev.data as
          | { type: 'stored'; id: string }
          | { type: 'storage-error'; id: string; error: string }
          | { type: 'progress'; id: string; progress: number }
          | { type: 'complete'; id: string }
          | { type: 'error'; id: string; error: string }
          | { type: 'all-complete' };

        if (!data || !('type' in data)) return;

        // Handle worker completion and termination
        if (data.type === 'all-complete') {
          this.terminateWorker();
          return;
        }

        const affected =
          data && 'id' in data ? this.uploadQueue.get(data.id) : undefined;
        if (!affected) return;

        if (data.type === 'stored') {
          affected.status = 'stored';
          affected.updatedAt = new Date();
          this.notifyListeners('stored', affected);
        } else if (data.type === 'storage-error') {
          affected.status = 'failed';
          affected.error = data.error;
          affected.updatedAt = new Date();
          this.notifyListeners('error', affected);

          this.showGlobalSnackbar(
            `Storage failed for "${affected.metadata.name}": ${data.error}`,
            'error',
            2000
          );
        } else if (data.type === 'progress') {
          affected.status = 'uploading';
          affected.progress = data.progress;
          affected.updatedAt = new Date();
          // Persist status to IndexedDB with resilience
          void (async () => {
            try {
              await this.indexedDBService.updateFileStatus(
                affected.id,
                'uploading'
              );
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              if (msg.includes('File not found in IndexedDB')) {
                // Create missing record, then update
                try {
                  await this.indexedDBService.storeFile(
                    affected.file,
                    affected.id
                  );
                  await this.indexedDBService.updateFileStatus(
                    affected.id,
                    'uploading'
                  );
                } catch (storeErr) {
                  const log = {
                    t: new Date().toISOString(),
                    severity: 'WARNING',
                    message:
                      'IndexedDB create+update (uploading) failed after missing record',
                    error:
                      storeErr instanceof Error
                        ? storeErr.message
                        : String(storeErr),
                    stack_trace:
                      storeErr instanceof Error
                        ? (storeErr.stack ?? null)
                        : null,
                    trace_id: affected.id,
                  };
                  console.log(JSON.stringify(log));
                }
              } else {
                const log = {
                  t: new Date().toISOString(),
                  severity: 'WARNING',
                  message: 'IndexedDB status update (uploading) failed',
                  error: msg,
                  stack_trace:
                    err instanceof Error ? (err.stack ?? null) : null,
                  trace_id: affected.id,
                };
                console.log(JSON.stringify(log));
              }
            }
          })();
          this.notifyListeners('progress', affected);
        } else if (data.type === 'complete') {
          affected.status = 'uploaded';
          affected.progress = 100;
          affected.updatedAt = new Date();
          // Persist status to IndexedDB as 'completed' with resilience
          void (async () => {
            try {
              await this.indexedDBService.updateFileStatus(
                affected.id,
                'completed'
              );
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              if (msg.includes('File not found in IndexedDB')) {
                try {
                  await this.indexedDBService.storeFile(
                    affected.file,
                    affected.id
                  );
                  await this.indexedDBService.updateFileStatus(
                    affected.id,
                    'completed'
                  );
                } catch (storeErr) {
                  const log = {
                    t: new Date().toISOString(),
                    severity: 'WARNING',
                    message:
                      'IndexedDB create+update (completed) failed after missing record',
                    error:
                      storeErr instanceof Error
                        ? storeErr.message
                        : String(storeErr),
                    stack_trace:
                      storeErr instanceof Error
                        ? (storeErr.stack ?? null)
                        : null,
                    trace_id: affected.id,
                  };
                  console.log(JSON.stringify(log));
                }
              } else {
                const log = {
                  t: new Date().toISOString(),
                  severity: 'WARNING',
                  message: 'IndexedDB status update (completed) failed',
                  error: msg,
                  stack_trace:
                    err instanceof Error ? (err.stack ?? null) : null,
                  trace_id: affected.id,
                };
                console.log(JSON.stringify(log));
              }
            }
          })();
          this.notifyListeners('complete', affected);

          // Show success snackbar (2 seconds)
          this.showGlobalSnackbar(
            `"${affected.metadata.name}" uploaded successfully`,
            'success',
            2000
          );

          // Note: Worker handles cleanup after this specific file's upload succeeds
          // The cleanup happens in the worker immediately after xhrUpload succeeds for this file
          // Each file's cleanup is independent and happens after its own retry logic completes
        } else if (data.type === 'error') {
          affected.status = 'failed';
          affected.error = data.error;
          affected.updatedAt = new Date();
          // Persist status to IndexedDB as 'failed' with resilience
          void (async () => {
            try {
              await this.indexedDBService.updateFileStatus(
                affected.id,
                'failed'
              );
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              if (msg.includes('File not found in IndexedDB')) {
                try {
                  await this.indexedDBService.storeFile(
                    affected.file,
                    affected.id
                  );
                  await this.indexedDBService.updateFileStatus(
                    affected.id,
                    'failed'
                  );
                } catch (storeErr) {
                  const log = {
                    t: new Date().toISOString(),
                    severity: 'WARNING',
                    message:
                      'IndexedDB create+update (failed) failed after missing record',
                    error:
                      storeErr instanceof Error
                        ? storeErr.message
                        : String(storeErr),
                    stack_trace:
                      storeErr instanceof Error
                        ? (storeErr.stack ?? null)
                        : null,
                    trace_id: affected.id,
                  };
                  console.log(JSON.stringify(log));
                }
              } else {
                const log = {
                  t: new Date().toISOString(),
                  severity: 'WARNING',
                  message: 'IndexedDB status update (failed) failed',
                  error: msg,
                  stack_trace:
                    err instanceof Error ? (err.stack ?? null) : null,
                  trace_id: affected.id,
                };
                console.log(JSON.stringify(log));
              }
            }
          })();
          this.notifyListeners('error', affected);

          // Show error snackbar (2 seconds)
          this.showGlobalSnackbar(
            `Upload failed for "${affected.metadata.name}": ${data.error}`,
            'error',
            2000
          );
        }
      });
    } catch (e) {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Failed to initialize upload worker',
        error: e instanceof Error ? e.message : String(e),
        stack_trace: e instanceof Error ? (e.stack ?? null) : null,
        trace_id: 'n/a',
      };
      console.log(JSON.stringify(log));
      throw e;
    }
  }

  private terminateWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      console.log('Upload worker terminated after all uploads completed');
    }
  }

  // Main-thread upload has been disabled per requirements.
  // All uploads are handled by the Web Worker for better performance and reliability.

  private notifyListeners(event: string, item: UnifiedUploadItem): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(item);
        } catch (error) {
          console.log('Error in event listener:', error);
        }
      });
    }
  }

  /**
   * Show snackbar notification using global window function
   * This works even when DocumentUploadManager is unmounted
   */
  private showGlobalSnackbar(
    message: string,
    variant: 'success' | 'error' | 'info' | 'warning',
    autoHideDuration: number = 2000
  ): void {
    if (typeof window !== 'undefined' && (window as any).showSnackbar) {
      try {
        (window as any).showSnackbar(message, {
          variant,
          autoHideDuration,
        });
      } catch (error) {
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'Failed to show snackbar',
          error: error instanceof Error ? error.message : String(error),
          stack_trace: error instanceof Error ? (error.stack ?? null) : null,
          trace_id: 'n/a',
        };
        console.log(JSON.stringify(log));
      }
    }
  }

  private getWorkerCode(): string {
    return `
// Worker-side FIFO queue with concurrency control
const uploadQueue = [];
const activeUploads = new Map();
let maxConcurrentUploads = 3;

// IndexedDB configuration constants
const DB_NAME = 'DocUploadFileStore';
const STORE_NAME = 'files';

async function getFileFromOPFS(id) {
  try {
    const root = await navigator.storage.getDirectory();
    for await (const [name, handle] of root.entries()) {
      if (name.startsWith(id + '-')) {
        try {
          const file = await handle.getFile();
          return file;
        } catch (err) {
          // File might have been deleted/moved - skip and continue
          if (err && err.name === 'NotFoundError') continue;
          throw err;
        }
      }
    }
    return null;
  } catch (err) {
    // OPFS not available or access denied
    return null;
  }
}

async function retrieveFile(id) {
  // Retry logic to handle race condition where file was just stored
  // and OPFS directory iteration might not see it immediately
  let attempts = 0;
  const maxAttempts = 3;
  const retryDelay = 100; // ms

  while (attempts < maxAttempts) {
    const file = await getFileFromOPFS(id);
    if (file) return file;

    attempts++;
    if (attempts < maxAttempts) {
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, retryDelay * attempts));
    }
  }

  return null; // File not found after retries
}

function post(evt) {
  self.postMessage(evt);
}

function xhrUpload(file, cfg) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 100);
        post({ type: 'progress', id: cfg.id, progress });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        post({ type: 'complete', id: cfg.id });
        resolve();
      } else {
        const err = parseUploadError(xhr);
        const error = new Error(err);
        error.statusCode = xhr.status;
        error.isClientError = xhr.status >= 400 && xhr.status < 500;
        reject(error);
      }
    };
    xhr.onerror = () => {
      const err = 'Network error during upload';
      reject(new Error(err));
    };
    xhr.ontimeout = () => {
      const err = 'Upload timeout';
      reject(new Error(err));
    };

    const formData = new FormData();
    formData.append('file', file);

    xhr.open('POST', cfg.endpoint);
    if (cfg.bearerToken) xhr.setRequestHeader('Authorization', \`Bearer \${cfg.bearerToken}\`);
    if (cfg.headers) {
      for (const [k, v] of Object.entries(cfg.headers)) {
        const key = k.toLowerCase();
        if (key !== 'content-type' && key !== 'authorization' && v != null) {
          xhr.setRequestHeader(k, v);
        }
      }
    }

    xhr.timeout = cfg.timeout ?? 30000;
    xhr.send(formData);
  });
}

function parseUploadError(xhr) {
  try {
    const response = JSON.parse(xhr.responseText);
    return (
      response.message ||
      response.error ||
      response.detail ||
      \`Server error (\${xhr.status}): \${xhr.statusText}\`
    );
  } catch {
    return xhr.statusText || \`Upload failed with status \${xhr.status}\`;
  }
}


async function cleanupUploadedFile(fileId) {
  // Clean up a specific file after successful upload
  try {
    // Guard: Check if this file is still in the queue or actively uploading
    // This prevents race conditions where multiple uploads of the same file might be queued
    const isInQueue = uploadQueue.some(task => task.id === fileId);
    const isActivelyUploading = activeUploads.has(fileId);

    if (isInQueue || isActivelyUploading) {
      // Skip cleanup - file is still being processed
      const log = {
        t: new Date().toISOString(),
        severity: 'INFO',
        message: 'Skipping cleanup for file - still in queue or uploading',
        error: null,
        stack_trace: null,
        trace_id: fileId,
      };
      console.log(JSON.stringify(log));
      return;
    }

    // Delete from OPFS first
    // Note: This cleanup is protected by the guard above that checks if the file is still
    // in the queue or actively uploading, preventing concurrent access race conditions
    const root = await navigator.storage.getDirectory();
    let opfsDeleted = false;

    try {
      for await (const [name, handle] of root.entries()) {
        if (name.startsWith(fileId + '-')) {
          // Use try-catch for removeEntry to handle potential concurrent deletion
          try {
            await root.removeEntry(name);
            opfsDeleted = true;
            break;
          } catch (removeErr) {
            // File might have been deleted by another operation or external script - safe to ignore
            if (removeErr && (removeErr.name === 'NotFoundError' || removeErr.name === 'InvalidStateError')) {
              break;
            }
            throw removeErr;
          }
        }
      }
    } catch (opfsErr) {
      // File might not exist in OPFS, continue with IndexedDB cleanup
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'OPFS cleanup failed',
        error: opfsErr instanceof Error ? opfsErr.message : String(opfsErr),
        stack_trace: opfsErr instanceof Error ? (opfsErr.stack ?? null) : null,
        trace_id: fileId
      };
      console.log(JSON.stringify(log));
    }

    // Delete from IndexedDB
    try {
      const db = await new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      await new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const deleteRequest = store.delete(fileId);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
      });

      db.close();
    } catch (idbErr) {
      // IndexedDB might not be available or file doesn't exist
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'IndexedDB cleanup failed',
        error: idbErr instanceof Error ? idbErr.message : String(idbErr),
        stack_trace: idbErr instanceof Error ? (idbErr.stack ?? null) : null,
        trace_id: fileId
      };
      console.log(JSON.stringify(log));
    }
  } catch (err) {
    // Cleanup is best-effort, don't fail the upload if it fails
    const log = {
      t: new Date().toISOString(),
      severity: 'WARNING',
      message: 'Failed to cleanup uploaded file',
      error: err instanceof Error ? err.message : String(err),
      stack_trace: err instanceof Error ? (err.stack ?? null) : null,
      trace_id: fileId
    };
    console.log(JSON.stringify(log));
  }
}

async function cleanupOldFiles() {
  const TWENTY_MINUTES_MS = 20 * 60 * 1000;
  const now = Date.now();

  try {
    // Open IndexedDB to get files with uploaded_at timestamp
    const db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    // Get all files from IndexedDB
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const allFiles = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Filter files that need to be cleaned up (older than 20 minutes, any status)
    // Note: Successfully uploaded files are already cleaned up immediately after upload
    // This catches failed uploads, stuck pending files, or any orphaned files
    const filesToCleanup = allFiles.filter(file => {
      if (!file.createdAt) return false;
      const age = now - file.createdAt;
      return age > TWENTY_MINUTES_MS;
    });

    if (filesToCleanup.length === 0) {
      db.close();
      return;
    }

    // Delete files from OPFS
    // Note: Only old files (20+ minutes) are cleaned up, so risk of concurrent access is minimal
    const root = await navigator.storage.getDirectory();
    for (const file of filesToCleanup) {
      try {
        // Find and delete from OPFS with race condition protection
        for await (const [name, handle] of root.entries()) {
          if (name.startsWith(file.id + '-')) {
            try {
              await root.removeEntry(name);
              break;
            } catch (removeErr) {
              // File might have been deleted by another cleanup or external script - safe to ignore
              if (removeErr && (removeErr.name === 'NotFoundError' || removeErr.name === 'InvalidStateError')) {
                break;
              }
              throw removeErr;
            }
          }
        }

        // Delete from IndexedDB - create a new transaction for each deletion
        // because the previous transaction becomes inactive after async operations
        await new Promise((resolve, reject) => {
          const deleteTransaction = db.transaction([STORE_NAME], 'readwrite');
          const deleteStore = deleteTransaction.objectStore(STORE_NAME);
          const deleteRequest = deleteStore.delete(file.id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        });
      } catch (err) {
        // Continue cleanup even if one file fails
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'Failed to cleanup file',
          error: err instanceof Error ? err.message : String(err),
          stack_trace: err instanceof Error ? (err.stack ?? null) : null,
          trace_id: file.id
        };
        console.log(JSON.stringify(log));
      }
    }

    db.close();
  } catch (err) {
    // Cleanup is a best-effort operation, don't fail the upload if it fails
    const log = {
      t: new Date().toISOString(),
      severity: 'WARNING',
      message: 'Failed to cleanup old files',
      error: err instanceof Error ? err.message : String(err),
      stack_trace: err instanceof Error ? (err.stack ?? null) : null,
      trace_id: 'n/a'
    };
    console.log(JSON.stringify(log));
  }
}

async function processUpload(task) {
  const { id } = task;
  let retries = 0;

  while (retries < task.maxRetries) {
    try {
      const file = await retrieveFile(id);
      if (!file) {
        // File not found in OPFS - this can happen if:
        // 1. File was never stored (unlikely if we got this far)
        // 2. File was already uploaded and cleaned up (race condition)
        // 3. File was deleted by user while upload was queued
        // Only report error if this is the first attempt (not a retry)
        if (retries === 0) {
          post({ type: 'error', id, error: 'File not found in OPFS/IndexedDB' });
        }
        return { success: false };
      }
      await xhrUpload(file, task);
      return { success: true }; // Success
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';

      // Check if this is a client error (4xx) that should not be retried
      // Client errors indicate issues like: unsupported file type, unauthorized, file too large, etc.
      if (err && err.isClientError) {
        post({ type: 'error', id, error: errorMessage });
        return { success: false }; // Exit immediately without retry
      }

      retries++;
      if (retries >= task.maxRetries) {
        post({ type: 'error', id, error: errorMessage });
      } else {
        // Wait before retry (exponential backoff) for server errors (5xx) or network issues
        await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, retries - 1), 10000)));
      }
    }
  }

  // All retries exhausted
  return { success: false };
}

// Track pending cleanup operations to ensure they complete before worker termination
const pendingCleanups = new Map();

async function processQueue() {
  // Process up to current concurrency limit
  while (uploadQueue.length > 0 && activeUploads.size < maxConcurrentUploads) {
    const task = uploadQueue.shift();
    if (!task) break;

    const uploadPromise = processUpload(task).then(async result => {
      activeUploads.delete(task.id);
      // Only cleanup if upload was successful
      // Failed uploads are kept in storage so user can retry
      if (result?.success) {
        // Track cleanup operation to prevent premature worker termination
        // Cleanup runs immediately; main thread updates IndexedDB status asynchronously
        const cleanupPromise = cleanupUploadedFile(task.id).finally(() => {
          pendingCleanups.delete(task.id);
        });
        pendingCleanups.set(task.id, cleanupPromise);

        // Don't await cleanup - let it run async but track it
        cleanupPromise.catch(err => {
          // Cleanup errors are already logged in cleanupUploadedFile
        });
      }
      // Continue processing queue after this upload completes
      processQueue();
    }).catch(() => {
      // processUpload handles all errors internally, this is just a safety net
      activeUploads.delete(task.id);
      processQueue();
    });

    activeUploads.set(task.id, uploadPromise);
  }

  // If queue is empty and no active uploads, wait for any pending cleanups then terminate
  if (uploadQueue.length === 0 && activeUploads.size === 0) {
    // Wait for all pending cleanup operations to complete
    if (pendingCleanups.size > 0) {
      await Promise.allSettled(Array.from(pendingCleanups.values()));
    }

    post({ type: 'all-complete' });
    self.close();
  }
}

async function storeFileInOPFSAndIndexedDB(fileData) {
  const { id, file, metadata } = fileData;

  try {
    // Store in OPFS
    const root = await navigator.storage.getDirectory();
    const safeName = metadata.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = \`\${id}-\${safeName}\`;

    try {
      const fileHandle = await root.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(file);
      await writable.close();
    } catch (writeErr) {
      // Handle NotFoundError or other OPFS errors
      if (writeErr && writeErr.name === 'NotFoundError') {
        throw new Error('OPFS directory became unavailable during write');
      }
      throw writeErr;
    }

    // Store metadata in IndexedDB
    const db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.put({
        id,
        fileName: metadata.name,
        fileSize: metadata.size,
        fileType: metadata.type,
        uploadStatus: 'pending',
        createdAt: Date.now(),
        lastModified: metadata.lastModified,
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();

    post({ type: 'stored', id });
    return true;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Storage failed';
    post({ type: 'storage-error', id, error: errorMsg });
    return false;
  }
}

function calculateTimeout(fileSize) {
  // Base timeout for network overhead
  const BASE_TIMEOUT_MS = 60000; // 60 seconds

  // Dynamic timeout based on file size using conservative speed assumption
  // Unified calculation with main thread (FileUploadService.calculateFileTimeout):
  // 50 KB/s = 50 * 1024 bytes/second = 51,200 bytes/second = 51.2 bytes/millisecond
  // This matches the conservative speed assumption in the main thread for consistency.
  // NOTE: This is intentionally conservative for slower networks. Modern speeds are typically 500+ KB/s.
  // Formula: fileSize (bytes) / 51.2 bytes/ms ≈ fileSize / 51.2
  // Capped at 30 minutes (1,800,000 ms) to match main thread MAX_TIMEOUT_MS
  const BYTES_PER_MS = (50 * 1024) / 1000; // 50 KB/s = 51.2 bytes/ms
  const MAX_TIMEOUT_MS = 1800000; // 30 minutes maximum (matching main thread)
  const MIN_TIMEOUT_MS = 120000; // 2 minutes minimum (matching main thread)

  const sizeBasedTimeout = fileSize / BYTES_PER_MS;
  const timeoutWithBuffer = sizeBasedTimeout * 1.5; // 50% buffer for network variability
  const calculatedTimeout = BASE_TIMEOUT_MS + timeoutWithBuffer;

  return Math.min(Math.max(calculatedTimeout, MIN_TIMEOUT_MS), MAX_TIMEOUT_MS);
}

self.onmessage = async (ev) => {
  const cmd = ev.data;
  if (!cmd) return;

  if (cmd.type === 'store-and-upload-batch') {
    // Handle batch of files: store all, clean old files right before upload
    const { filesData, uploadConfig } = cmd.payload;

    if (
      uploadConfig &&
      typeof uploadConfig.concurrency === 'number' &&
      uploadConfig.concurrency > 0
    ) {
      maxConcurrentUploads = Math.floor(uploadConfig.concurrency);
    } else {
      maxConcurrentUploads = 3;
    }

    // Step 1: Store all files in OPFS and IndexedDB in parallel, track successfully stored files
    const storagePromises = filesData.map(async (fileData) => ({
      fileData,
      stored: await storeFileInOPFSAndIndexedDB(fileData),
    }));

    const storageResults = await Promise.allSettled(storagePromises);
    const successfullyStoredFiles = storageResults
      .filter((result) => result.status === 'fulfilled' && result.value.stored)
      .map((result) => result.value.fileData);

    // Step 2: Enqueue only successfully stored files for upload (check for duplicates)
    for (const fileData of successfullyStoredFiles) {
      // Guard: Don't enqueue if already in queue or actively uploading
      const alreadyQueued = uploadQueue.some(task => task.id === fileData.id);
      const alreadyUploading = activeUploads.has(fileData.id);

      if (alreadyQueued || alreadyUploading) {
        const log = {
          t: new Date().toISOString(),
          severity: 'WARNING',
          message: 'Skipping duplicate enqueue for file',
          error: null,
          stack_trace: null,
          trace_id: fileData.id,
        };
        console.log(JSON.stringify(log));
        continue;
      }

      uploadQueue.push({
        id: fileData.id,
        endpoint: uploadConfig.endpoint,
        bearerToken: uploadConfig.bearerToken,
        headers: uploadConfig.headers,
        timeout: calculateTimeout(fileData.file.size),
        maxRetries: uploadConfig.maxRetries ?? 3,
      });
    }

    // Step 3: Clean up old files (older than 20 minutes) right before starting uploads
    await cleanupOldFiles();

    // Step 4: Start processing with concurrency limit (3 at a time)
    processQueue();
  } else if (cmd.type === 'enqueue') {
    // Add to FIFO queue (for files already stored) - check for duplicates
    const { id } = cmd.payload;
    const alreadyQueued = uploadQueue.some(task => task.id === id);
    const alreadyUploading = activeUploads.has(id);

    if (alreadyQueued || alreadyUploading) {
      const log = {
        t: new Date().toISOString(),
        severity: 'WARNING',
        message: 'Skipping duplicate enqueue for file',
        error: null,
        stack_trace: null,
        trace_id: id,
      };
      console.log(JSON.stringify(log));
      return;
    }

    uploadQueue.push(cmd.payload);
    // Start processing
    processQueue();
  }
};
`;
  }
}
